import Add16 from '@carbon/icons-react/es/add/16';
import UserMultiple20 from '@carbon/icons-react/es/user--multiple/20';
import { Row } from 'antd';
import { keyBy, orderBy } from 'lodash';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { SubscriptionStatus } from 'api/billing/types';
import { CostRateSourceType } from 'api/cost-rates';
import { PermissionSetType, UserStatus } from 'api/models';
import { Operation } from 'api/permissions';
import { Button } from 'components/atoms/button';
import { SearchInput } from 'components/atoms/input';
import { FilterBar } from 'components/molecules/filter-bar';
import { Page } from 'components/molecules/page';
import { HeaderWithIconTitle } from 'components/molecules/page/page';
import { FilterBarWrapper } from 'components/organisms/archived-projects/archived-projects-list/styles';
import { GENERAL_API_STALE } from 'config/query-config';
import { Member } from 'data/members/api';
import { useMemberPermission } from 'hooks/permissions';
import {
  useAccount,
  useBillingFeatureFlag,
  useIsAccountActive,
} from 'hooks/use-account';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import {
  useAvailableLicensesQuery,
  useSubscriptionQuery,
} from 'queries/billing';
import { useNativeMembersQuery } from 'queries/members';
import { useVendorRolesQuery } from 'queries/roles';
import { fullName } from 'utils/full-name';
import { Card, Title } from '../billing';
import { ManageCostRates } from '../cost-rates';
import { TeamMemberDrawerModal } from './drawer-modal/drawer-modal';
import { TeamMembersInviteWrapper } from './invite-modal/wrapper';
import { TeamMemberList } from './list';
import { TeamMemberTabs } from './tabs';
import { TeamMemberDrawerStateProps, TeamMembersTabsEnum } from './types';
import { usePermissionOptions } from './use-permission';

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-02);
`;
const SideBySideWrapper = styled.div``;
const LicenseCard = styled(Card)`
  height: 112px;
  ${SideBySideWrapper} {
    display: flex;
    flex-direction: row;
    gap: var(--spacing-07);
  }
`;

const StyledRow = styled(Row)`
  padding-bottom: var(--layout-03);
`;

const Header = styled.span`
  align-items: center;
  display: flex;
  flex: 1;
`;

const PageContent = styled(Page.Content)`
  padding: var(--layout-01);
`;

const FilterBarWrapperWithoutPadding = styled(FilterBarWrapper)`
  padding: 0;
  margin-bottom: var(--layout-02);
  box-shadow: none;
`;

const SearchInputWithMargin = styled(SearchInput)`
  margin-right: var(--spacing-05);
  .ant-btn {
    border: none;
  }
`;

/*
const CTALink = styled.a`
  align-items: center;
  display: flex;

  > svg {
    margin-right: var(--spacing-03);
  }
`; */

const LicenseDetails = ({
  totalLicenses,
  availableLicenses,
  usedByPartners,
  usedByVendor,
}: {
  totalLicenses?: number;
  availableLicenses: number;
  usedByVendor: number;
  usedByPartners: number;
}) => {
  const { company: vendorCompany } = useAccount();

  return (
    <StyledRow>
      <LicenseCard span={8}>
        <div>
          <Title>
            <FormattedMessage id="billing.total.licenses.title" />
          </Title>
          <b>{totalLicenses ?? 0}</b>
        </div>
        <div>
          <Title>
            <FormattedMessage id="billing.total.licenses.caption" />
          </Title>
        </div>
      </LicenseCard>
      <LicenseCard span={8}>
        <div>
          <Title>
            <FormattedMessage id="billing.available.licenses.title" />
          </Title>
          <b>{availableLicenses}</b>
        </div>
        <div>
          <Title>
            <FormattedMessage
              id="billing.available.licenses.caption"
              values={{
                licenseCount: availableLicenses,
              }}
            />
          </Title>
        </div>
      </LicenseCard>
      <LicenseCard span={8}>
        <SideBySideWrapper>
          <TitleWrapper>
            <Title>
              <FormattedMessage
                id="billing.licenses.use_by_vendor.title"
                values={{ companyName: vendorCompany.companyName }}
              />
            </Title>
            <b>{usedByVendor}</b>
          </TitleWrapper>
          <TitleWrapper>
            <Title>
              <FormattedMessage id="billing.licenses.use_by_partners.title" />
            </Title>
            <b>{usedByPartners}</b>
          </TitleWrapper>
        </SideBySideWrapper>
      </LicenseCard>
    </StyledRow>
  );
};

export const VendorMembersSettingsPage: FC = () => {
  const title = useTranslation('settings.team_members.title');
  const [tab, setActiveTab] = useState(TeamMembersTabsEnum.Active);
  const [searchValue, setSearchValue] = useState('');
  const [filteredMembers, setFilteredMembers] = useState<Member[]>([]);
  const [userIdForCostRate, setUserIdForCostRate] =
    useState<number | undefined>(undefined);
  const openCostRateManager = useCallback((id: number) => {
    setUserIdForCostRate(id);
  }, []);
  const closeCostRateManager = useCallback(() => {
    setUserIdForCostRate(undefined);
  }, []);
  const searchPlaceholderText = useTranslation(
    'settings.team_members.search_placeholder',
  );
  const { data, isLoading } = useNativeMembersQuery();
  const memberPermission = useMemberPermission();
  const isBillingEnabled = useBillingFeatureFlag();
  const { data: subscription } = useSubscriptionQuery();
  const availableLicenses = useAvailableLicensesQuery();

  const currentUser = useUser();
  const isAdmin =
    currentUser?.permissions?.permissionSet?.permissionSetType ===
    PermissionSetType.AccountAdmin;

  const filter = useMemo(() => {
    return {
      active: (_members: Member[]) =>
        _members.filter(({ user }) => user.userStatus === UserStatus.Active),
      invited: (_members: Member[]) =>
        _members.filter(({ user }) => user.userStatus === UserStatus.Invited),
      deactivated: (_members: Member[]) =>
        _members.filter(({ user }) => user.userStatus === UserStatus.Inactive),
    };
  }, []);

  const userLookup = useMemo(() => {
    if (data == null) {
      return undefined;
    }

    return keyBy(data.users, 'userId');
  }, [data]);

  const filterFromSearchValue = useCallback(
    (members: Member[]) => {
      return members?.filter((member) =>
        !searchValue.trim()
          ? true
          : fullName(member.user)
              ?.toLowerCase()
              ?.includes(searchValue?.toLowerCase()) ||
            member.user.role?.roleName
              ?.toLowerCase()
              ?.includes(searchValue?.toLowerCase()) ||
            member.user.emailId?.includes(searchValue?.toLowerCase()) ||
            member.permissionSet?.permissionSetName
              ?.toLowerCase()
              ?.includes(searchValue?.toLowerCase()),
      );
    },
    [searchValue],
  );

  const [teamMemberDrawerState, setTeamMemberDrawerState] =
    useState<TeamMemberDrawerStateProps>({
      visible: false,
      readOnly: false,
      selected: undefined,
    });

  const onClose = () => {
    setTeamMemberDrawerState((prevState) => ({
      ...prevState,
      visible: false,
    }));
  };

  const changeToInviteMode = () => {
    setTeamMemberDrawerState({
      visible: true,
      readOnly: false,
      selected: undefined,
    });
  };

  const changeToEditMode = (member: Member) => {
    setTeamMemberDrawerState({
      visible: true,
      readOnly: false,
      selected: member,
    });
  };

  const changeToViewMode = (member: Member) => {
    setTeamMemberDrawerState({
      visible: true,
      readOnly: true,
      selected: member,
    });
  };

  const onChange = (e) => setSearchValue(e.target.value);

  const _onUserInvited = useCallback(() => {
    if (!teamMemberDrawerState?.selected) {
      setActiveTab(TeamMembersTabsEnum.Invited);
    }
  }, [teamMemberDrawerState?.selected]);

  const isAccountActive = useIsAccountActive();
  const canShowInviteButton = useMemo(
    () => memberPermission.includes(Operation.Create) && isAccountActive,
    [isAccountActive, memberPermission],
  );

  useEffect(() => {
    setFilteredMembers(filterFromSearchValue(filter[tab](data?.members || [])));
  }, [filter, data, tab, filterFromSearchValue]);
  const permissionsList = usePermissionOptions();
  const { data: rawRoles = [] } = useVendorRolesQuery({
    staleTime: GENERAL_API_STALE,
  });
  const roles = useMemo(
    () =>
      orderBy(
        rawRoles.map(({ roleName, roleId }) => ({
          label: roleName,
          value: roleId,
        })),
        [({ label }) => label.toLowerCase()],
        ['asc'],
      ),
    [rawRoles],
  );

  const licensesUsedByVendor =
    (subscription?.usedQuantity.LICENSE ?? 0) -
    (subscription?.partnerUsedQuantity ?? 0);
  const licensesUsedByPartners = subscription?.partnerUsedQuantity ?? 0;

  return (
    <Page>
      <Helmet>{title}</Helmet>
      <Page.Header
        sticky
        $white
        title={
          <Header>
            <HeaderWithIconTitle icon={<UserMultiple20 />} title={title} />
            {/* TODO:: KB_LINK. Uncomment once link is ready */}
            {/* <CTALink
              href={KBLinks.teamMembers}
              target="_blank"
              rel="noreferrer"
            >
              <Launch16 />
              <FormattedMessage id="settings.team_members.header.cta" />
            </CTALink> */}
          </Header>
        }
      />

      <PageContent>
        {isBillingEnabled &&
          isAdmin &&
          subscription &&
          subscription.subscriptionStatus !==
            SubscriptionStatus['IN-TRIAL'] && (
            <LicenseDetails
              availableLicenses={availableLicenses}
              totalLicenses={subscription?.licenseQuantity}
              usedByPartners={licensesUsedByPartners}
              usedByVendor={licensesUsedByVendor}
            />
          )}
        <FilterBarWrapperWithoutPadding>
          <FilterBar.Primary>
            <TeamMemberTabs
              tab={tab}
              onChangeTab={setActiveTab}
              members={data?.members || []}
            />
          </FilterBar.Primary>
          <FilterBar.Secondary>
            <SearchInputWithMargin
              disabled={isLoading}
              placeholder={searchPlaceholderText}
              size="small"
              onChange={onChange}
              value={searchValue}
              direction="right"
            />
            {canShowInviteButton && (
              <Button
                icon={<Add16 />}
                iconPlacement="right"
                type="primary"
                onClick={changeToInviteMode}
              >
                <FormattedMessage id="settings.team_members.invite_members_cta" />
              </Button>
            )}
          </FilterBar.Secondary>
        </FilterBarWrapperWithoutPadding>
        <TeamMemberList
          memberData={filteredMembers}
          showMemberDetail={changeToViewMode}
          openCostRateManager={openCostRateManager}
        />
        {teamMemberDrawerState?.visible &&
          (isBillingEnabled ? (
            subscription && (
              <TeamMembersInviteWrapper
                subscription={subscription}
                availableLicenses={availableLicenses}
                drawerState={teamMemberDrawerState}
                onClose={onClose}
                showMemberDetail={changeToViewMode}
                changeToEditMode={changeToEditMode}
                onUserInvited={_onUserInvited}
                drawerSectionProps={{}}
                permissionOptions={permissionsList}
                roleOptions={roles}
                isVendorInvite
              />
            )
          ) : (
            <TeamMemberDrawerModal
              drawerState={teamMemberDrawerState}
              onClose={onClose}
              showMemberDetail={changeToViewMode}
              changeToEditMode={changeToEditMode}
              onUserInvited={_onUserInvited}
              drawerSectionProps={{}}
              permissionOptions={permissionsList}
              roleOptions={roles}
              isVendorInvite
            />
          ))}
        {userIdForCostRate != null && userLookup != null && (
          <ManageCostRates
            sourceType={CostRateSourceType.User}
            sourceId={userIdForCostRate}
            resourceName={fullName(userLookup[userIdForCostRate] ?? '')}
            userLookup={userLookup}
            onClose={closeCostRateManager}
          />
        )}
      </PageContent>
    </Page>
  );
};
